import { useContext, useEffect, useMemo } from 'react';
import { type LineString, featureCollection } from '@turf/helpers';
import { Layer, Source } from 'react-map-gl';
import { MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import useMap from '../../../hooks/useMap';
import { useDispatch, useSelector } from '../../../redux';
import { setActiveSegmentId, setHoveredSegmentId } from '../../../redux/customRoute';
import { updateCursor } from '../../../redux/reducer';
import { LineProperties } from '../../../types/CustomRoute';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import { hoveredPolylineId } from '../../../utils/layers';
import getLineStrings from './getLineStrings';

export const getSourceId = (uniqueId?: string) => (uniqueId ? `polyline-source-${uniqueId}` : 'polyline-source');

/**
 * HoveredPolyline renders a transparent layer over ActivePolyline and InactivePolyline components.
 * By having a single transparent layer handle mouse events, we avoid:
 * 1. Duplicate event handlers on each underlying polyline (ActivePolyline renders 3 PolylineSources which is 6 Layers, and InactivePolyline renders 1 PolylineSource which is 2 Layers)
 * 2. Race conditions between multiple hover states
 * 3. Edge cases when hovering between segments
 * This pattern simplifies event handling by centralizing hover logic in one place.
 */

const HoveredPolyline = () => {
  const map = useMap();
  const dispatch = useDispatch();
  const { activeSegmentId, segments, isEditing } = useSelector(state => ({
    activeSegmentId: state.customRoute.activeSegmentId,
    segments: state.customRoute.route.segments,
    isEditing: state.customRoute.isEditing
  }));
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  useEffect(() => {
    const onClick = (event: MapMouseEvent) => {
      if (event.features && event.features.length > 0) {
        event.preventDefault();
        const feature = event.features?.[0];
        const properties = feature?.properties as LineProperties;

        // Extract segment ID from the feature ID
        // Feature ID format: "segment-${segmentId}-path-${pathIndex}"
        const featureId = properties?.id;
        const segmentIdMatch = featureId?.match(/segment-(\d+)-path-/);

        if (segmentIdMatch) {
          const segmentId = parseInt(segmentIdMatch[1], 10);
          if (activeSegmentId === segmentId) {
            dispatch(setActiveSegmentId({ activeSegmentId: undefined, canEdit: false }));
          } else {
            dispatch(setActiveSegmentId({ activeSegmentId: segmentId, canEdit: false }));
          }
        }
      }
    };

    const onMouseMove = (event: MapMouseEvent) => {
      if (event.features && event.features.length > 0) {
        const feature = event.features?.[0];
        const properties = feature?.properties as LineProperties;
        const featureId = properties?.id;
        const segmentIdMatch = featureId?.match(/segment-(\d+)-path-/);
        if (segmentIdMatch) {
          const segmentId = parseInt(segmentIdMatch[1], 10);
          dispatch(setHoveredSegmentId(segmentId));
        }
      }
      dispatch(updateCursor('pointer'));
    };

    const onMouseLeave = () => {
      dispatch(setHoveredSegmentId(undefined));
      dispatch(updateCursor('unset'));
    };

    registerEventHandler(hoveredPolylineId, 'click', onClick);
    registerEventHandler(hoveredPolylineId, 'mousemove', onMouseMove);
    registerEventHandler(hoveredPolylineId, 'mouseleave', onMouseLeave);

    return () => {
      unregisterEventHandler(hoveredPolylineId, 'click', onClick);
      unregisterEventHandler(hoveredPolylineId, 'mousemove', onMouseMove);
      unregisterEventHandler(hoveredPolylineId, 'mouseleave', onMouseLeave);
    };
  }, [activeSegmentId, registerEventHandler, unregisterEventHandler, dispatch]);

  useEffect(() => {
    const removeActiveStates = (event: MapMouseEvent | MapTouchEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        dispatch(setActiveSegmentId({ activeSegmentId: undefined, canEdit: false }));
      }
    };

    map?.on('click', removeActiveStates);
    return () => {
      map?.off('click', removeActiveStates);
    };
  }, [dispatch, map]);

  const data = useMemo(() => featureCollection<LineString, LineProperties>(segments?.map(segment => getLineStrings(segment)).flat()), [segments]);
  const sourceId = getSourceId(hoveredPolylineId);

  return (
    <Source id={sourceId} type="geojson" data={data} lineMetrics>
      <Layer
        id={hoveredPolylineId}
        source={sourceId}
        type="line"
        layout={{
          'line-join': 'round',
          'line-cap': 'round',
          visibility: isEditing ? 'none' : 'visible'
        }}
        paint={{
          'line-width': 12, // Wider than default for better hover detection
          'line-opacity': 0, // Completely transparent
          'line-color': 'rgba(0, 0, 0, 0)'
        }}
      />
    </Source>
  );
};

export default HoveredPolyline;
